// SCME v1.0 · Fase 3 · Diálogo de exportación de SCME_EXPORT.zip
// ---------------------------------------------------------------------------
// Flujo:
//   1. Preview  → GET manifest previsto + blacklist aplicada (dry-run).
//   2. Confirmar → POST /api/scme/export (requiere PIN maestro; el
//      interceptor axios global pide el PIN si es necesario).
//   3. Descarga directa del blob ZIP y muestra el SHA-256 devuelto por
//      el backend (cabecera X-SCME-SHA256).
//
// Restricciones respetadas:
//   • No instala nada.
//   • No sincroniza con otros proyectos.
//   • Muestra la blacklist textual como recordatorio corporativo.
// ---------------------------------------------------------------------------
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Download, ShieldCheck, FileArchive, Hash, X } from "lucide-react";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PluginsExportDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      setPreview(null);
      setLastResult(null);
      try {
        const resp = await api.post("/scme/export/preview");
        if (!cancel) setPreview(resp.data);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Error preparando el paquete");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const resp = await api.post("/scme/export", null, { responseType: "blob" });
      const sha = resp.headers?.["x-scme-sha256"] || resp.headers?.["X-SCME-SHA256"] || "";
      const size = resp.data?.size || 0;
      const disp = resp.headers?.["content-disposition"] || "";
      const match = disp.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : "SCME_EXPORT.zip";

      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setLastResult({ filename, sha, size });
      toast.success("SCME_EXPORT.zip descargado");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo generar el ZIP");
    } finally {
      setDownloading(false);
    }
  };

  const manifest = preview?.manifest;
  const entries = preview?.entries || [];
  const blacklist = preview?.blacklist;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="scme-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <FileArchive className="h-5 w-5 text-orange-600" />
            Exportar SCME_EXPORT.zip
          </DialogTitle>
          <DialogDescription className="text-xs">
            Paquete de conocimiento arquitectónico · sólo metadata, sin código ni secretos.
          </DialogDescription>
        </DialogHeader>

        {loading || !preview ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-1">
                Proyecto de origen
              </div>
              <div className="font-mono text-[13px] text-neutral-800">{manifest?.sourceProject}</div>
              <div className="mt-2 grid grid-cols-2 gap-3 text-[11px]">
                <div>
                  <span className="text-neutral-500">SCME version:</span>{" "}
                  <span className="font-mono">{manifest?.scmeVersion}</span>
                </div>
                <div>
                  <span className="text-neutral-500">Plugins:</span>{" "}
                  <span className="font-mono">{manifest?.counts?.plugins}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-neutral-500">Generado por:</span>{" "}
                  <span className="font-mono">{manifest?.generatedBy}</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-neutral-200 bg-white p-3">
              <div className="flex items-center gap-2 mb-2">
                <FileArchive className="h-4 w-4 text-neutral-500" />
                <h4 className="font-semibold text-sm text-neutral-800">Contenido previsto del ZIP</h4>
              </div>
              <ul className="space-y-1" data-testid="scme-export-entries">
                <li className="flex items-center justify-between text-[12px] bg-neutral-50 rounded px-2 py-1.5">
                  <code className="font-mono text-neutral-800">manifest.json</code>
                  <span className="text-[10px] text-neutral-500">generado ahora</span>
                </li>
                {entries.map((e) => (
                  <li key={e.name} className="flex items-center justify-between text-[12px] bg-neutral-50 rounded px-2 py-1.5">
                    <code className="font-mono text-neutral-800">{e.name}</code>
                    <span className="text-[10px] text-neutral-500 font-mono">
                      {e.size} B · {e.sha256.slice(0, 10)}…
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="h-4 w-4 text-emerald-700" />
                <h4 className="font-semibold text-sm text-emerald-900">Blacklist aplicada</h4>
              </div>
              <p className="text-[11px] text-emerald-900 mb-2">
                El paquete se rechazará automáticamente si detecta cualquiera de estos patrones o claves.
              </p>
              <div className="flex flex-wrap gap-1">
                {(blacklist?.patterns || []).map((p) => (
                  <code key={p} className="text-[10px] font-mono bg-white text-emerald-800 border border-emerald-200 rounded px-1.5 py-0.5">
                    {p}
                  </code>
                ))}
                {(blacklist?.jsonKeys || []).map((k) => (
                  <code key={k} className="text-[10px] font-mono bg-white text-emerald-800 border border-emerald-200 rounded px-1.5 py-0.5">
                    {k}
                  </code>
                ))}
              </div>
            </div>

            {lastResult && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Hash className="h-4 w-4 text-blue-700" />
                  <h4 className="font-semibold text-sm text-blue-900">Último paquete generado</h4>
                </div>
                <div className="text-[12px] text-blue-900 space-y-0.5">
                  <div><span className="text-blue-700">Archivo:</span> <code className="font-mono">{lastResult.filename}</code></div>
                  <div><span className="text-blue-700">Tamaño:</span> <code className="font-mono">{lastResult.size} B</code></div>
                  <div className="break-all"><span className="text-blue-700">SHA-256:</span> <code className="font-mono">{lastResult.sha}</code></div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                data-testid="scme-export-cancel"
                className="px-3 py-2 text-sm rounded-lg border border-neutral-300 hover:bg-neutral-50"
              >
                Cerrar
              </button>
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                data-testid="scme-export-confirm"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60"
              >
                {downloading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Generando…</>
                ) : (
                  <><Download className="h-4 w-4" /> Descargar ZIP</>
                )}
              </button>
            </div>

            <p className="text-[10px] text-neutral-500 italic">
              La adopción en el proyecto destino siempre requiere una copia validada por humano. SCME no instala nada automáticamente.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
